import { useCallback, useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import type { PrototypeDeployStatus, PrototypeLogLine } from '@aiai/shared';
import { formatSeconds } from '../lib/format';
import { usePrototypeLogSocket } from '../hooks/usePrototypeLogSocket';
import { BuildOutputLog } from './PrototypeBuildOutput';
import { Spinner } from './Spinner';

const STEPS = [
  { key: 'provisioning', label: 'Provisioning' },
  { key: 'building', label: 'Building' },
  { key: 'live', label: 'Live' },
] as const;

/** Client-side cap, matching the server's build buffer. */
const MAX_BUILD_LINES = 500;

/**
 * Prominent loader shown while a prototype deploy is in flight
 * (provisioning/building). Renders nothing for any other status, so the
 * detail page's polling hides it as soon as the deploy settles. The API
 * reports no build percentage, so this is a step indicator plus an elapsed
 * timer measured from the deployment's last status change (approximate).
 *
 * With a `prototypeId` it also shows a collapsible, auto-scrolling "Build
 * output" panel: the `/prototype-logs` socket streams the build feed while the
 * deployment is provisioning/building (lines so far, then new ones live).
 */
export function PrototypeDeployProgress({
  deployStatus,
  since,
  prototypeId,
}: {
  deployStatus: PrototypeDeployStatus;
  since: string;
  prototypeId?: string;
}) {
  const inFlight = deployStatus === 'provisioning' || deployStatus === 'building';
  const [now, setNow] = useState(() => Date.now());
  const [outputOpen, setOutputOpen] = useState(true);
  const [lines, setLines] = useState<PrototypeLogLine[]>([]);
  const [streamError, setStreamError] = useState<string | null>(null);

  useEffect(() => {
    if (!inFlight) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [inFlight]);

  const onLine = useCallback((line: PrototypeLogLine) => {
    setStreamError(null);
    setLines((prev) =>
      prev.length >= MAX_BUILD_LINES
        ? [...prev.slice(prev.length - MAX_BUILD_LINES + 1), line]
        : [...prev, line],
    );
  }, []);

  // A (re)subscribe replays the server snapshot, so start from a clean slate.
  const streaming = inFlight && outputOpen && !!prototypeId;
  useEffect(() => {
    if (streaming) setLines([]);
  }, [streaming]);

  usePrototypeLogSocket(prototypeId, streaming, {
    onLine,
    onError: (message) => setStreamError(message),
    onReconnect: () => setLines([]),
  });

  if (!inFlight) return null;

  const activeIndex = STEPS.findIndex((s) => s.key === deployStatus);
  const sinceMs = Date.parse(since);
  const elapsed = Number.isNaN(sinceMs) ? null : formatSeconds((now - sinceMs) / 1000);
  const heading =
    deployStatus === 'building' ? 'Building your prototype…' : 'Provisioning your prototype…';

  return (
    <div className="rounded-xl border border-line bg-surface-2 px-5 py-5">
      <div role="status" aria-live="polite" className="flex items-start gap-4">
        <Spinner className="h-8 w-8 shrink-0 text-info" />
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-ink">{heading}</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            This usually takes a few minutes — this page updates automatically.
            {elapsed && <span className="ml-1 tabular-nums">Started {elapsed} ago.</span>}
          </p>

          <ol className="mt-4 flex flex-wrap items-center gap-2 text-xs" aria-label="Deploy progress">
            {STEPS.map((step, i) => {
              const done = i < activeIndex;
              const active = i === activeIndex;
              return (
                <li
                  key={step.key}
                  className="flex items-center gap-2"
                  aria-current={active ? 'step' : undefined}
                  data-state={done ? 'done' : active ? 'active' : 'pending'}
                >
                  {i > 0 && <span className="h-px w-6 bg-line" aria-hidden="true" />}
                  <span
                    className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${
                      active
                        ? 'border-ink font-medium text-ink'
                        : done
                          ? 'border-line text-ink-muted'
                          : 'border-line text-ink-faint'
                    }`}
                  >
                    {done && <Check className="h-3 w-3 text-success" aria-hidden="true" />}
                    {active && <Spinner className="h-3 w-3" />}
                    {step.label}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      {prototypeId && (
        <div className="mt-4">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted hover:text-ink"
            aria-expanded={outputOpen}
            onClick={() => setOutputOpen((o) => !o)}
          >
            {outputOpen ? (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            )}
            Build output
            {lines.length > 0 && <span className="text-ink-faint">({lines.length} lines)</span>}
          </button>
          {outputOpen && (
            <div className="mt-2">
              <BuildOutputLog lines={lines} emptyMessage="Waiting for build output…" />
              {streamError && <p className="mt-1 text-xs text-bad">{streamError}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
